import { useEffect } from 'react';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { HStack, LookoBadge, VStack } from '@looko-design-system';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@mui/icons-material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Collapse, IconButton, ListItemText, Stack, Typography, styled } from '@mui/material';
import type { Theme } from '@mui/material';
import type { CSSObject } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import { convertNewlineToJSX } from '@toss/react';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { isEqual } from 'lodash-es';
import { useRecoilState, useRecoilValue } from 'recoil';

import { isDeploy } from '@/environment';
import useUserState from '@/hook/useUserState';
import Cafe24MasterMenus from '@/router/cafe24-master-account/Cafe24MasterMenus';
import { roleManager, roleViewer } from '@/router/consts';
import ManagerMenus from '@/router/manager/ManagerMenus';
import { orderMap } from '@/router/orderMap';
import type { SidebarMenu } from '@/router/types';
import { isSidebarOpenState } from '@/state/isSidebarOpenState';
import { useShopAuth } from '@/state/userAuthContext';

import useFilteredMenuByAuthority from '../../../router/sidebar';
import LogoSection from '../logo';
import { openedMenuAtom } from './openedMenuAtom';

export const SIDEBAR_WIDTH = 240 as const;
export const FOLDED_SIDEBAR_WIDTH = 56 as const;

const isHoverSlidebarAtom = atom(false);

function selectedNavMenu(location: string, nav: string) {
  return location.includes(nav);
}
/**
 *
 */
export function ListCollapse({ title, path, icon, childrenMenus }: SidebarMenu) {
  // router
  const location = useLocation();

  const isOpenSidebarState = useRecoilValue(isSidebarOpenState);
  const isHover = useAtomValue(isHoverSlidebarAtom);

  const [openedMenu, setOpenedMenu] = useAtom(openedMenuAtom);

  const isOpenSidebar = isOpenSidebarState || isHover;

  // local
  const open = openedMenu === title;
  const filteredMenus = childrenMenus?.filter(menu => menu.display);

  // handler
  const handleClick = () => {
    setOpenedMenu(prev => {
      if (title === prev) {
        return null;
      }
      return title;
    });
  };

  useEffect(() => {
    const pattern = /^(\/[-\w]+)(\/|$)/;
    const match = location.pathname.match(pattern);

    if (match) {
      const extractedWord = match[1];
      if (extractedWord === path) {
        setOpenedMenu(title);
      }
    }
  }, [location, path, setOpenedMenu, title]);

  return (
    <>
      <ListItem
        onClick={handleClick}
        sx={{
          cursor: 'pointer',
          svg: {
            color: 'white',
          },

          '&:hover': {
            background: '#122e46',
          },
          ...(open && {
            backgroundColor: '#122e46',
            color: 'primary.main',
            svg: {
              color: 'primary.main',
            },
          }),
          justifyContent: 'space-between',
        }}
      >
        <HStack id={`사이드바-${title.replace(/[\s()]/g, '')}-메뉴`} gap={1.8} alignItems={'center'}>
          {icon}

          <ListItemText primary={<Typography variant="Title_Looko">{convertNewlineToJSX(title)}</Typography>} />
        </HStack>

        {open ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse component="li" in={open && isOpenSidebar} timeout="auto" unmountOnExit>
        <List disablePadding>
          {filteredMenus?.map(router => (
            <ListItem key={router.title} disablePadding>
              <ListItemButton
                component={Link}
                to={router.path}
                sx={theme => ({
                  ...(selectedNavMenu(location.pathname, router.path) && {
                    backgroundColor: theme.palette.primary.main,
                  }),
                  '&:hover': {
                    background: '#122e46',
                  },

                  pl: 6.8,
                })}
              >
                <ListItemText
                  primary={
                    <Typography variant="Title_Looko" whiteSpace={'pre-line'}>
                      {router.title}
                    </Typography>
                  }
                />
                {!!router.count && (
                  <LookoBadge
                    color={
                      router.path === orderMap['duplicatedOrder'].path
                        ? selectedNavMenu(location.pathname, router.path)
                          ? 'red2'
                          : 'red1'
                        : selectedNavMenu(location.pathname, router.path)
                          ? 'secondary'
                          : 'primary'
                    }
                    label={router.count > 999 ? '999+' : router.count}
                  />
                )}
                {router.badge}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Collapse>
    </>
  );
}

const openedMixin = (theme: Theme): CSSObject => ({
  width: SIDEBAR_WIDTH,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  width: FOLDED_SIDEBAR_WIDTH,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: prop => prop !== 'open' })(({ theme, open }) => ({
  width: SIDEBAR_WIDTH,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

/**
 *
 */
export default function Sidebar({ headerHeight }: { headerHeight: number }) {
  const user = useUserState();
  const shop = useShopAuth();

  const [isOpenSidebarSetting, setIsOpenSidebarSetting] = useRecoilState(isSidebarOpenState);
  const [isHover, setIsHover] = useAtom(isHoverSlidebarAtom);

  const isOpenSidebar = isOpenSidebarSetting || isHover;

  const isFocusButtonRef = React.useRef(false);
  const timer = React.useRef<NodeJS.Timeout | null>(null);

  const isCafe24Master = React.useMemo(() => {
    return isEqual(user?.userID, Number(import.meta.env.VITE_CAFE_24_ONLY_ACCOUNT_USER_ID));
  }, [user]);

  const isManager = React.useMemo(() => {
    return ((user?.roles.includes(roleManager) || user?.roles.includes(roleViewer)) && shop.shopID === '') ?? false;
  }, [shop.shopID, user?.roles]);

  const ownerMenus = useFilteredMenuByAuthority();
  const menus = !isCafe24Master ? (isManager ? ManagerMenus : ownerMenus) : Cafe24MasterMenus;

  const onMouseEnter = () => {
    if (isOpenSidebarSetting) {
      return;
    }

    if (timer.current) {
      clearTimeout(timer.current);
    }

    timer.current = setTimeout(() => {
      setIsHover(true);
    }, 100);
  };

  const onMouseLeave = () => {
    if (timer.current) {
      clearTimeout(timer.current);
    }

    timer.current = setTimeout(() => {
      if (isOpenSidebarSetting || isFocusButtonRef.current) {
        return;
      }
      setIsHover(false);
    }, 100);
  };

  const handleSidebar = () => {
    setIsOpenSidebarSetting(prev => !prev);
  };

  React.useEffect(() => {
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, []);

  return (
    <Drawer
      variant="permanent"
      anchor="left"
      open={isOpenSidebar}
      sx={{
        backgroundColor: '#112E46',
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          zIndex: 100,
        },
        position: 'absolute',
      }}
      ModalProps={{ keepMounted: true }}
    >
      <Stack flex={1} bgcolor={'#062137'} color={'white'} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        <List sx={{ flex: 1 }} disablePadding>
          <ListItem
            disablePadding={true}
            sx={theme => ({
              height: `${headerHeight}px`,
              justifyContent: 'space-between',

              transform: `translateX(-${SIDEBAR_WIDTH}px)`,
              padding: 0,
              ...(isOpenSidebar && {
                paddingLeft: 2,
                transform: 'translateX(0px)',
              }),

              transition: theme.transitions.create('all', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            })}
          >
            <LogoSection />
          </ListItem>

          {menus.map(router => (
            <MenuItem key={router.path} {...router} />
          ))}
        </List>

        <VStack p={2} spacing={1}>
          <Typography variant={'caption'}>
            {'버전: '}

            {/* XXX: 성능 시험 표기용 */}

            <>
              {_APP_VERSION}
              {_APP_GIT_SHA ? `+${_APP_GIT_SHA}` : ''}
            </>

            {!isDeploy && (
              <>
                {`-${import.meta.env.MODE}`}
                <br />
                {`(${new Date(_BUILD_TIME_MS).toLocaleString()})`}
              </>
            )}
          </Typography>

          {!isDeploy && <Link to={'/dev'}>개발자메뉴</Link>}
        </VStack>
      </Stack>

      <Stack
        onMouseEnter={() => {
          isFocusButtonRef.current = true;
        }}
        onMouseLeave={() => {
          isFocusButtonRef.current = false;
          onMouseLeave();
        }}
        width={FOLDED_SIDEBAR_WIDTH}
        minWidth={FOLDED_SIDEBAR_WIDTH}
        height={FOLDED_SIDEBAR_WIDTH}
        alignItems={'center'}
        justifyContent={'center'}
        position={'absolute'}
        right={0}
      >
        <IconButton onClick={handleSidebar} sx={{ width: '100%', height: '100%' }}>
          <Stack
            alignItems={'center'}
            justifyContent={'center'}
            width={16}
            height={24}
            bgcolor={'#41586b'}
            borderRadius={10}
            sx={{ color: 'white' }}
          >
            {isOpenSidebarSetting ? <ArrowLeftOutlined /> : <ArrowRightOutlined />}
          </Stack>
        </IconButton>
      </Stack>
    </Drawer>
  );
}

const MenuItem = React.memo(function MenuItem(router: SidebarMenu) {
  const location = useLocation();

  const setOpenedMenu = useSetAtom(openedMenuAtom);

  if (router.childrenMenus) {
    return (
      <ListCollapse title={router.title} path={router.path} icon={router.icon} childrenMenus={router.childrenMenus} />
    );
  } else {
    return (
      <ListItem
        disablePadding
        sx={{
          borderRadius: '5px',
        }}
        onClick={() => setOpenedMenu(router.title)}
      >
        <ListItemButton
          component={Link}
          to={router.path}
          sx={{
            ...(selectedNavMenu(location.pathname, router.path) && {
              backgroundColor: '#122e46',
              color: 'primary.main',
              svg: {
                color: 'primary.main',
              },
            }),
            '&:hover': {
              background: '#122e46',
            },
          }}
        >
          <HStack key={router.path} id={`사이드바-${router.title}-메뉴`} gap={1.8} alignItems={'center'} width={'100%'}>
            {router.icon}

            <ListItemText primary={<Typography variant="Title_Looko">{router.title}</Typography>} />
          </HStack>
        </ListItemButton>
      </ListItem>
    );
  }
});
